import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";
import { AppContext } from "../../context";
import {
  CATALOG_UPDATE_CSV_FIELDS,
  createStoreCatalogUpdate,
  generateStoreCatalogUpdate,
  getStoreCatalogGlbInventory,
  getStoreCatalogUpdate,
  getStoreCatalogUpdateTaskLog,
  getStoreCatalogUpdates,
  publishStoreCatalogUpdate,
  renderStoreCatalogUpdateImages,
  updateStoreCatalogUpdateCsvs,
  uploadStoreCatalogGlbs,
} from "../sales/service";
import { fmtRelativeTime, toErrorText } from "./storeSalesDashboardHelpers";

const CSV_LABELS = {
  cp: "cp.csv",
  cp_sizes: "cp_sizes.csv",
  ranges: "ranges.csv",
  cp_features: "cp_features.csv",
  cp_packets: "cp_packets.csv",
  hardwares: "hardwares.csv",
  product_rendering: "product_rendering.csv",
  property_material_mapper: "property_material_mapper.csv",
  drawer_mapper: "drawer_mapper.csv",
};

const IN_FLIGHT_STATUSES = ["draft", "generating", "generated", "publishing"];

const STAGE_TABS = [
  { key: "generator", label: "Generate" },
  { key: "renderer", label: "Render" },
  { key: "publish", label: "Publish" },
];

const stageStatusColor = (status) => {
  switch (status) {
    case "completed":
      return "success";
    case "running":
      return "warning";
    case "failed":
      return "danger";
    default:
      return "secondary";
  }
};

const stageStatusLabel = (status) => {
  switch (status) {
    case "completed":
      return "Completed";
    case "running":
      return "Running";
    case "failed":
      return "Failed";
    default:
      return "Pending";
  }
};

const isUpdateRunning = (update) =>
  ["generating", "publishing"].includes(update.status) ||
  update.image_render_status === "rendering";

const canReuploadCsvs = (update) =>
  update.status === "draft" ||
  (update.current && update.status !== "completed");

const statusColor = (status) => {
  switch (status) {
    case "completed":
      return "success";
    case "generated":
      return "info";
    case "generating":
    case "publishing":
    case "rendering":
      return "warning";
    case "draft":
      return "secondary";
    case "failed":
      return "danger";
    default:
      return "light";
  }
};

const CatalogUpdateErrorDetails = ({ title, text }) => {
  if (!text) return null;
  return (
    <Alert color="danger" className="small py-2" fade={false}>
      {title ? <div className="fw-semibold mb-1">{title}</div> : null}
      <pre
        className="small mb-0 bg-transparent border-0 p-0 text-danger text-wrap overflow-auto"
        style={{ maxHeight: "16rem", whiteSpace: "pre-wrap" }}
      >
        {text}
      </pre>
    </Alert>
  );
};

const imageRenderFailureDetails = (stats) => {
  const errors = stats?.errors;
  if (!Array.isArray(errors) || errors.length === 0) return null;

  return errors
    .map((entry) => {
      const lines = [
        `CP: ${entry.cp_code ?? "—"}`,
        `Product ID: ${entry.product_id ?? "—"}`,
      ];
      if (Array.isArray(entry.pending) && entry.pending.length > 0) {
        lines.push(`Pending images: ${entry.pending.join(", ")}`);
      }
      if (entry.error) lines.push(`Error: ${entry.error}`);
      if (entry.backtrace) lines.push(`Stack trace:\n${entry.backtrace}`);
      return lines.join("\n");
    })
    .join("\n\n");
};

const publishFailureDetails = (stats) => {
  const errors = stats?.errors;
  if (!Array.isArray(errors) || errors.length === 0) return null;

  return errors
    .map((entry) => {
      const lines = [
        `CP: ${entry.cp_code ?? "—"}`,
        `Product ID: ${entry.product_id ?? "—"}`,
      ];
      if (entry.store_product_id) {
        lines.push(`Store product ID: ${entry.store_product_id}`);
      }
      if (entry.error) lines.push(`Error: ${entry.error}`);

      return lines.join("\n");
    })
    .join("\n\n");
};

const publishStatsSummary = (stats) => {
  if (!stats || typeof stats !== "object") return null;
  const published = stats.published;
  const failed = stats.failed;
  if (published == null && failed == null) return null;

  return `Published: ${published ?? 0}, failed: ${failed ?? 0}`;
};

const isPublishErrorMessage = (message) => {
  if (!message) return false;

  return message.includes("Context: publish");
};

const catalogUpdateErrorLinks = (update) => {
  const links = [];
  if (update.error_message && !isPublishErrorMessage(update.error_message)) {
    links.push({ tab: "generator", label: "Generate error — view details" });
  }
  if (
    update.image_render_error ||
    imageRenderFailureDetails(update.image_render_stats)
  ) {
    links.push({ tab: "renderer", label: "Image render issues — view details" });
  }
  if (isPublishErrorMessage(update.error_message) || publishFailureDetails(update.publish_stats)) {
    links.push({ tab: "publish", label: "Publish issues — view details" });
  }

  return links;
};

const defaultDetailTabForUpdate = (update) => {
  const links = catalogUpdateErrorLinks(update);
  if (links.length > 0) {
    return links[0].tab;
  }

  return "generator";
};

const sortGlbInventoryEntries = (entries) =>
  [...(entries || [])].sort((a, b) => {
    if (a.present !== b.present) return a.present ? 1 : -1;
    return a.cp_code.localeCompare(b.cp_code);
  });

const imageRenderStatsSummary = (stats) => {
  if (!stats || typeof stats !== "object") return null;
  const parts = [];
  if (stats.rendered != null) parts.push(`${stats.rendered} rendered`);
  if (stats.skipped != null) parts.push(`${stats.skipped} skipped`);
  if (stats.failed != null && stats.failed > 0) parts.push(`${stats.failed} failed`);
  if (stats.populated != null) parts.push(`${stats.populated} populated`);
  if (Array.isArray(stats.missing_glbs) && stats.missing_glbs.length > 0) {
    parts.push(`${stats.missing_glbs.length} missing GLBs`);
  }
  return parts.length ? parts.join(", ") : null;
};

const StoreCatalogUpdatesPage = () => {
  const { currentUser } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [filesByField, setFilesByField] = useState({});
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [actionId, setActionId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [scopedCpCodes, setScopedCpCodes] = useState("");
  const [renderSubmitting, setRenderSubmitting] = useState(false);
  const [glbModalOpen, setGlbModalOpen] = useState(false);
  const [glbInventory, setGlbInventory] = useState(null);
  const [glbLoading, setGlbLoading] = useState(false);
  const [glbUploading, setGlbUploading] = useState(false);
  const [glbError, setGlbError] = useState(null);
  const glbUploadInputRef = useRef(null);
  const [updateCsvOpen, setUpdateCsvOpen] = useState(false);
  const [updateCsvForId, setUpdateCsvForId] = useState(null);
  const [updateCsvFilesByField, setUpdateCsvFilesByField] = useState({});
  const [updateCsvSubmitting, setUpdateCsvSubmitting] = useState(false);
  const [updateCsvError, setUpdateCsvError] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);
  const [detailUpdate, setDetailUpdate] = useState(null);
  const [detailTab, setDetailTab] = useState("generator");
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [logUpdateId, setLogUpdateId] = useState(null);
  const [logStage, setLogStage] = useState(null);
  const [logContent, setLogContent] = useState("");
  const [logLoading, setLogLoading] = useState(false);
  const [logRefreshing, setLogRefreshing] = useState(false);
  const [logError, setLogError] = useState(null);
  const logPreRef = useRef(null);

  const refreshDetailUpdate = useCallback(async (updateId) => {
    setDetailLoading(true);
    setDetailError(null);
    try {
      const payload = await getStoreCatalogUpdate(updateId);
      if (payload.catalog_update) {
        setDetailUpdate(payload.catalog_update);
      }
    } catch (e) {
      setDetailError(toErrorText(e, "Unable to load catalog update details."));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const openDetailModal = (update, tab = "generator") => {
    setDetailTab(tab);
    setDetailUpdate(update);
    refreshDetailUpdate(update.id);
  };

  const closeDetailModal = () => {
    setDetailUpdate(null);
    setDetailError(null);
  };

  const loadTaskLog = useCallback(async (updateId, stage, { initial = false } = {}) => {
    setLogError(null);
    if (initial) {
      setLogContent("");
      setLogLoading(true);
    } else {
      setLogRefreshing(true);
    }

    try {
      const payload = await getStoreCatalogUpdateTaskLog(updateId, stage);
      setLogContent(payload.content || "");
    } catch (e) {
      setLogError(toErrorText(e, "Unable to load task log."));
    } finally {
      if (initial) {
        setLogLoading(false);
      } else {
        setLogRefreshing(false);
      }
    }
  }, []);

  const openLogModal = (updateId, stage) => {
    setLogUpdateId(updateId);
    setLogStage(stage);
    setLogModalOpen(true);
    loadTaskLog(updateId, stage, { initial: true });
  };

  const refreshLog = () => {
    if (logUpdateId == null || !logStage) {
      return;
    }

    loadTaskLog(logUpdateId, logStage, { initial: false });
  };

  const closeLogModal = () => {
    setLogModalOpen(false);
    setLogUpdateId(null);
    setLogStage(null);
    setLogContent("");
    setLogError(null);
    setLogRefreshing(false);
  };

  useLayoutEffect(() => {
    if (!logModalOpen || logLoading || !logPreRef.current) {
      return;
    }

    logPreRef.current.scrollTop = logPreRef.current.scrollHeight;
  }, [logModalOpen, logLoading, logContent]);

  const loadUpdates = useCallback(async ({ refreshId = null } = {}) => {
    if (refreshId != null) {
      setRefreshingId(refreshId);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const payload = await getStoreCatalogUpdates();
      const rows = Array.isArray(payload?.catalog_updates) ? payload.catalog_updates : [];
      setUpdates(rows);
      setDetailUpdate((current) => {
        if (!current) {
          return current;
        }
        const refreshed = rows.find((row) => row.id === current.id);

        return refreshed ? { ...current, ...refreshed } : current;
      });
    } catch (e) {
      setError(toErrorText(e, "Unable to load catalog updates."));
    } finally {
      if (refreshId != null) {
        setRefreshingId(null);
      } else {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadUpdates();
  }, [loadUpdates]);

  const hasInFlight = useMemo(
    () => updates.some((u) => IN_FLIGHT_STATUSES.includes(u.status)),
    [updates]
  );

  const imageRendering = useMemo(
    () => updates.some((u) => u.image_render_status === "rendering"),
    [updates]
  );

  const loadGlbInventory = useCallback(async () => {
    setGlbLoading(true);
    setGlbError(null);
    try {
      const payload = await getStoreCatalogGlbInventory();
      setGlbInventory(payload);
    } catch (e) {
      setGlbError(toErrorText(e, "Unable to load GLB inventory."));
    } finally {
      setGlbLoading(false);
    }
  }, []);

  const openGlbModal = () => {
    setGlbModalOpen(true);
    setGlbError(null);
    loadGlbInventory();
  };

  const handleCreate = async () => {
    const hasAnyFile = CATALOG_UPDATE_CSV_FIELDS.some((f) => filesByField[f]);
    if (!hasAnyFile) {
      setCreateError("Select at least one CSV file.");
      return;
    }

    setCreateSubmitting(true);
    setCreateError(null);
    try {
      await createStoreCatalogUpdate(filesByField);
      setCreateOpen(false);
      setFilesByField({});
      await loadUpdates();
    } catch (e) {
      setCreateError(toErrorText(e, "Unable to create catalog update."));
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleGlbUpload = async (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selected.length) return;

    setGlbUploading(true);
    setGlbError(null);
    try {
      await uploadStoreCatalogGlbs(selected);
      await loadGlbInventory();
    } catch (e) {
      setGlbError(toErrorText(e, "Unable to upload GLB files."));
    } finally {
      setGlbUploading(false);
    }
  };

  const runRenderImages = async (updateId) => {
    setRenderSubmitting(true);
    setActionId(updateId);
    setActionError(null);
    try {
      const scope = scopedCpCodes.trim() || undefined;
      await renderStoreCatalogUpdateImages(updateId, { cpCodes: scope });
      await loadUpdates();
    } catch (e) {
      setActionError(toErrorText(e, "Unable to start image render."));
    } finally {
      setRenderSubmitting(false);
      setActionId(null);
    }
  };

  const runAction = async (id, action) => {
    setActionId(id);
    setActionError(null);
    try {
      const scope = scopedCpCodes.trim() || undefined;
      if (action === "generate") {
        await generateStoreCatalogUpdate(id, { cpCodes: scope });
      } else {
        await publishStoreCatalogUpdate(id, { cpCodes: scope });
      }
      await loadUpdates();
    } catch (e) {
      const fallback =
        action === "generate"
          ? "Unable to start generate."
          : "Unable to start publish.";
      setActionError(toErrorText(e, fallback));
    } finally {
      setActionId(null);
    }
  };

  const openUpdateCsvModal = (updateId) => {
    setUpdateCsvForId(updateId);
    setUpdateCsvFilesByField({});
    setUpdateCsvError(null);
    setUpdateCsvOpen(true);
  };

  const handleUpdateCsvs = async () => {
    const hasAnyFile = CATALOG_UPDATE_CSV_FIELDS.some((f) => updateCsvFilesByField[f]);
    if (!hasAnyFile) {
      setUpdateCsvError("Select at least one CSV file.");
      return;
    }
    if (!updateCsvForId) {
      setUpdateCsvError("No catalog update selected.");
      return;
    }

    setUpdateCsvSubmitting(true);
    setUpdateCsvError(null);
    try {
      await updateStoreCatalogUpdateCsvs(updateCsvForId, updateCsvFilesByField);
      setUpdateCsvOpen(false);
      setUpdateCsvFilesByField({});
      await loadUpdates();
    } catch (e) {
      setUpdateCsvError(toErrorText(e, "Unable to re-upload CSV files."));
    } finally {
      setUpdateCsvSubmitting(false);
    }
  };

  if (!currentUser?.admin) {
    return null;
  }

  const sortedGlbEntries = sortGlbInventoryEntries(glbInventory?.entries);

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="h4 mb-0">Catalog updates</h1>
        <div className="d-flex gap-2">
          <Button color="info" size="sm" onClick={openGlbModal}>
            Manage GLBs
          </Button>
          <Button
            color="primary"
            size="sm"
            onClick={() => {
              setCreateError(null);
              setCreateOpen(true);
            }}
            disabled={hasInFlight}
          >
            Create update
          </Button>
        </div>
      </div>

      <div className="mb-4">
        <label className="form-label small mb-1">
          Limit Generate / Publish / Render images to CP codes (optional)
        </label>
        <Input
          type="text"
          bsSize="sm"
          placeholder="e.g. 2d_wr_e1_i19, 1d_base_unit"
          value={scopedCpCodes}
          onChange={(e) => setScopedCpCodes(e.target.value)}
        />
        <p className="text-muted small mb-0 mt-1">
          Use <strong>Manage GLBs</strong> to upload <code>&lt;cp_code&gt;.glb</code>{" "}
          files to the render data folder. Render images runs from each update card
          after Generate (or on completed updates).
        </p>
      </div>

      {hasInFlight ? (
        <p className="text-muted small">
          Only one catalog update can be in progress at a time (draft through
          publishing).
        </p>
      ) : null}

      {error ? (
        <Alert color="danger" fade={false}>
          {error}
        </Alert>
      ) : null}

      {actionError ? (
        <Alert color="danger" fade={false}>
          {actionError}
          {actionId != null ? (
            <>
              {" "}
              <Button
                color="link"
                className="p-0 align-baseline"
                onClick={() => {
                  const update = updates.find((row) => row.id === actionId);
                  if (update) {
                    openDetailModal(
                      update,
                      isPublishErrorMessage(actionError) ? "publish" : "generator"
                    );
                  }
                }}
              >
                View details
              </Button>
            </>
          ) : null}
        </Alert>
      ) : null}

      {loading ? (
        <div className="py-5 text-center">
          <Spinner />
        </div>
      ) : (
        <div className="row g-3">
          {updates.map((update) => {
            const busy = actionId === update.id;
            const flags = update.provided_csv_flags || {};
            const canRenderImages =
              update.status === "generated" || update.status === "completed";
            const renderDisabled =
              busy ||
              renderSubmitting ||
              imageRendering ||
              update.image_render_status === "rendering";
            return (
              <div className="col-12 col-md-6 col-lg-4" key={update.id}>
                <Card
                  className="h-100 cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onClick={() => openDetailModal(update, defaultDetailTabForUpdate(update))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openDetailModal(update, defaultDetailTabForUpdate(update));
                    }
                  }}
                >
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <strong>#{update.id}</strong>
                      <div className="d-flex gap-1">
                        {update.current ? (
                          <Badge color="primary">current</Badge>
                        ) : null}
                        <Badge color={statusColor(update.status)}>
                          {update.status}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-muted small mb-2">
                      {fmtRelativeTime(update.created_at)}
                    </p>
                    {catalogUpdateErrorLinks(update).map((link) => (
                      <div className="mb-1" key={link.tab}>
                        <Button
                          color="link"
                          className="p-0 small text-danger text-start"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDetailModal(update, link.tab);
                          }}
                        >
                          {link.label}
                        </Button>
                      </div>
                    ))}
                    <CatalogUpdateErrorDetails
                      title="Generate / publish error"
                      text={update.error_message}
                    />
                    {update.image_render_status ? (
                      <div className="mb-2">
                        <Badge color={statusColor(update.image_render_status)}>
                          images: {update.image_render_status}
                        </Badge>
                      </div>
                    ) : null}
                    {isUpdateRunning(update) ? (
                      <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                        <span className="small text-muted d-flex align-items-center gap-1">
                          <Spinner size="sm" />
                          {update.image_render_status === "rendering"
                            ? "Rendering images in background…"
                            : "Running in background…"}
                        </span>
                        <Button
                          color="secondary"
                          outline
                          size="sm"
                          disabled={refreshingId === update.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            loadUpdates({ refreshId: update.id });
                          }}
                        >
                          {refreshingId === update.id ? (
                            <Spinner size="sm" />
                          ) : (
                            "Refresh"
                          )}
                        </Button>
                      </div>
                    ) : null}
                    <CatalogUpdateErrorDetails
                      title="Image render error"
                      text={update.image_render_error}
                    />
                    <CatalogUpdateErrorDetails
                      title="Image render failures"
                      text={imageRenderFailureDetails(update.image_render_stats)}
                    />
                    {imageRenderStatsSummary(update.image_render_stats) ? (
                      <p className="small text-muted mb-2">
                        {imageRenderStatsSummary(update.image_render_stats)}
                      </p>
                    ) : null}
                    {Array.isArray(update.image_render_stats?.rendered_images) &&
                    update.image_render_stats.rendered_images.length > 0 ? (
                      <div className="small mb-2">
                        <span className="text-muted">New images:</span>
                        <ul className="list-unstyled mb-0 mt-1">
                          {update.image_render_stats.rendered_images.map((name) => (
                            <li key={name}>
                              <code>{name}</code>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    <CatalogUpdateErrorDetails
                      title="Publish failures"
                      text={publishFailureDetails(update.publish_stats)}
                    />
                    {publishStatsSummary(update.publish_stats) ? (
                      <p className="small text-muted mb-2">
                        {publishStatsSummary(update.publish_stats)}
                      </p>
                    ) : null}
                    <ul className="list-unstyled small text-muted mb-3">
                      {CATALOG_UPDATE_CSV_FIELDS.map((field) => (
                        <li key={field}>
                          {CSV_LABELS[field]}
                          {flags[field] ? " (uploaded)" : ""}
                        </li>
                      ))}
                    </ul>
                    <div className="d-flex flex-wrap gap-2">
                      {canReuploadCsvs(update) ? (
                        <Button
                          color="secondary"
                          outline
                          size="sm"
                          disabled={busy || isUpdateRunning(update)}
                          onClick={(e) => {
                            e.stopPropagation();
                            openUpdateCsvModal(update.id);
                          }}
                        >
                          Re-upload CSVs
                        </Button>
                      ) : null}
                      {update.status === "draft" ? (
                        <Button
                          color="primary"
                          size="sm"
                          disabled={busy}
                          onClick={(e) => {
                            e.stopPropagation();
                            runAction(update.id, "generate");
                          }}
                        >
                          {busy ? <Spinner size="sm" /> : "Generate"}
                        </Button>
                      ) : null}
                      {canRenderImages ? (
                        <Button
                          color="info"
                          size="sm"
                          disabled={renderDisabled}
                          onClick={(e) => {
                            e.stopPropagation();
                            runRenderImages(update.id);
                          }}
                        >
                          {busy && renderSubmitting ? (
                            <Spinner size="sm" />
                          ) : (
                            "Render images"
                          )}
                        </Button>
                      ) : null}
                      {update.status === "generated" ? (
                        <Button
                          color="success"
                          size="sm"
                          disabled={busy}
                          onClick={(e) => {
                            e.stopPropagation();
                            runAction(update.id, "publish");
                          }}
                        >
                          {busy ? <Spinner size="sm" /> : "Publish"}
                        </Button>
                      ) : null}
                    </div>
                  </CardBody>
                </Card>
              </div>
            );
          })}
          {updates.length === 0 ? (
            <p className="text-muted">No catalog updates yet.</p>
          ) : null}
        </div>
      )}

      <Modal
        isOpen={createOpen}
        toggle={() => !createSubmitting && setCreateOpen(false)}
        size="lg"
      >
        <ModalHeader toggle={() => !createSubmitting && setCreateOpen(false)}>
          Create catalog update
        </ModalHeader>
        <ModalBody>
          <p className="text-muted small">
            Upload only the CSVs you changed. Omitted files are copied from the
            current catalog update.
          </p>
          {CATALOG_UPDATE_CSV_FIELDS.map((field) => (
            <div className="mb-3" key={field}>
              <label className="form-label small">{CSV_LABELS[field]}</label>
              <Input
                type="file"
                accept=".csv,text/csv"
                bsSize="sm"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  setFilesByField((prev) => ({
                    ...prev,
                    [field]: file || undefined,
                  }));
                }}
              />
            </div>
          ))}
          {createError ? (
            <Alert color="danger" fade={false}>
              {createError}
            </Alert>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            disabled={createSubmitting}
            onClick={() => setCreateOpen(false)}
          >
            Cancel
          </Button>
          <Button color="primary" disabled={createSubmitting} onClick={handleCreate}>
            {createSubmitting ? <Spinner size="sm" /> : "Create"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={updateCsvOpen}
        toggle={() => !updateCsvSubmitting && setUpdateCsvOpen(false)}
        size="lg"
      >
        <ModalHeader toggle={() => !updateCsvSubmitting && setUpdateCsvOpen(false)}>
          Re-upload CSVs for update #{updateCsvForId}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted small">
            Upload only corrected CSVs. Existing CSVs on this update are kept for omitted fields.
          </p>
          {CATALOG_UPDATE_CSV_FIELDS.map((field) => (
            <div className="mb-3" key={field}>
              <label className="form-label small">{CSV_LABELS[field]}</label>
              <Input
                type="file"
                accept=".csv,text/csv"
                bsSize="sm"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  setUpdateCsvFilesByField((prev) => ({
                    ...prev,
                    [field]: file || undefined,
                  }));
                }}
              />
            </div>
          ))}
          {updateCsvError ? (
            <Alert color="danger" fade={false}>
              {updateCsvError}
            </Alert>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            disabled={updateCsvSubmitting}
            onClick={() => setUpdateCsvOpen(false)}
          >
            Cancel
          </Button>
          <Button color="primary" disabled={updateCsvSubmitting} onClick={handleUpdateCsvs}>
            {updateCsvSubmitting ? <Spinner size="sm" /> : "Save CSVs"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={detailUpdate != null} toggle={closeDetailModal} size="lg">
        <ModalHeader toggle={closeDetailModal}>
          Catalog update #{detailUpdate ? detailUpdate.id : ""}
        </ModalHeader>
        <ModalBody>
          {detailError ? (
            <Alert color="danger" className="small py-2" fade={false}>
              {detailError}
            </Alert>
          ) : null}
          {detailLoading ? (
            <div className="py-2 text-center">
              <Spinner size="sm" />
            </div>
          ) : null}
          {detailUpdate ? (
            <>
              <Nav tabs className="mb-3">
                {STAGE_TABS.map((tab) => {
                  const stageInfo = detailUpdate.stage_summary
                    ? detailUpdate.stage_summary[tab.key]
                    : null;
                  const stageStatus = stageInfo ? stageInfo.status : "pending";
                  const completed = stageStatus === "completed";

                  return (
                    <NavItem key={tab.key}>
                      <NavLink
                        className={detailTab === tab.key ? "active" : ""}
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setDetailTab(tab.key);
                        }}
                      >
                        {completed ? "✓ " : ""}
                        {tab.label}
                      </NavLink>
                    </NavItem>
                  );
                })}
              </Nav>
              <TabContent activeTab={detailTab}>
                {STAGE_TABS.map((tab) => {
                  const stageInfo = detailUpdate.stage_summary
                    ? detailUpdate.stage_summary[tab.key]
                    : null;
                  const stageStatus = stageInfo ? stageInfo.status : "pending";

                  return (
                    <TabPane tabId={tab.key} key={tab.key}>
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <Badge color={stageStatusColor(stageStatus)}>
                          {stageStatusLabel(stageStatus)}
                        </Badge>
                        {stageStatus === "running" ? <Spinner size="sm" /> : null}
                      </div>
                      {tab.key === "generator" &&
                      detailUpdate.error_message &&
                      !isPublishErrorMessage(detailUpdate.error_message) ? (
                        <CatalogUpdateErrorDetails
                          title="Generate error"
                          text={detailUpdate.error_message}
                        />
                      ) : null}
                      {tab.key === "renderer" && detailUpdate.image_render_error ? (
                        <CatalogUpdateErrorDetails
                          title="Image render error"
                          text={detailUpdate.image_render_error}
                        />
                      ) : null}
                      {tab.key === "renderer" &&
                      imageRenderFailureDetails(detailUpdate.image_render_stats) ? (
                        <CatalogUpdateErrorDetails
                          title="Image render failures"
                          text={imageRenderFailureDetails(detailUpdate.image_render_stats)}
                        />
                      ) : null}
                      {tab.key === "renderer" && imageRenderStatsSummary(detailUpdate.image_render_stats) ? (
                        <p className="small text-muted mb-3">
                          {imageRenderStatsSummary(detailUpdate.image_render_stats)}
                        </p>
                      ) : null}
                      {tab.key === "renderer" &&
                      detailUpdate.image_render_stats &&
                      Array.isArray(detailUpdate.image_render_stats.rendered_images) &&
                      detailUpdate.image_render_stats.rendered_images.length > 0 ? (
                        <div className="small mb-3">
                          <span className="text-muted">New images:</span>
                          <ul className="list-unstyled mb-0 mt-1">
                            {detailUpdate.image_render_stats.rendered_images.map((name) => (
                              <li key={name}>
                                <code>{name}</code>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {tab.key === "publish" &&
                      isPublishErrorMessage(detailUpdate.error_message) ? (
                        <CatalogUpdateErrorDetails
                          title="Publish error"
                          text={detailUpdate.error_message}
                        />
                      ) : null}
                      {tab.key === "publish" &&
                      publishFailureDetails(detailUpdate.publish_stats) ? (
                        <CatalogUpdateErrorDetails
                          title="Publish failures"
                          text={publishFailureDetails(detailUpdate.publish_stats)}
                        />
                      ) : null}
                      {tab.key === "publish" && publishStatsSummary(detailUpdate.publish_stats) ? (
                        <p className="small text-muted mb-3">
                          {publishStatsSummary(detailUpdate.publish_stats)}
                        </p>
                      ) : null}
                      {stageInfo && stageInfo.log_available ? (
                        <Button
                          color="link"
                          className="p-0"
                          onClick={() => openLogModal(detailUpdate.id, tab.key)}
                        >
                          View logs
                        </Button>
                      ) : (
                        <p className="text-muted small mb-0">No log file for this stage yet.</p>
                      )}
                    </TabPane>
                  );
                })}
              </TabContent>
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={closeDetailModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={logModalOpen} toggle={closeLogModal} size="lg">
        <ModalHeader toggle={closeLogModal}>
          <div className="d-flex align-items-center justify-content-between w-100 me-3 gap-2">
            <span>
              {logStage
                ? `${STAGE_TABS.find((tab) => tab.key === logStage)?.label || logStage} log`
                : "Task log"}
            </span>
            <Button
              color="link"
              className="p-0 text-secondary"
              disabled={logLoading || logRefreshing || logUpdateId == null || !logStage}
              aria-label="Refresh log"
              onClick={refreshLog}
            >
              {logRefreshing ? (
                <Spinner size="sm" />
              ) : (
                <i className="fa fa-refresh" aria-hidden="true" />
              )}
            </Button>
          </div>
        </ModalHeader>
        <ModalBody>
          {logLoading ? (
            <div className="py-4 text-center">
              <Spinner />
            </div>
          ) : logError ? (
            <Alert color="danger" fade={false}>
              {logError}
            </Alert>
          ) : (
            <div
              ref={logPreRef}
              className="bg-light border rounded scrollbar"
              style={{ height: "60vh" }}
            >
              <pre className="small mb-0 p-3 d-block" style={{ whiteSpace: "pre-wrap" }}>
                {logContent || "Log file is empty."}
              </pre>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={closeLogModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={glbModalOpen}
        toggle={() => !glbUploading && setGlbModalOpen(false)}
        size="lg"
      >
        <ModalHeader toggle={() => !glbUploading && setGlbModalOpen(false)}>
          Manage GLBs
        </ModalHeader>
        <ModalBody>
          {glbInventory?.glb_root_path ? (
            <p className="text-muted small">
              GLB folder: <code>{glbInventory.glb_root_path}</code>
            </p>
          ) : null}
          <p className="text-muted small">
            One file per catalog product: <code>&lt;cp_code&gt;.glb</code>. Missing
            files are listed first.
          </p>
          <input
            ref={glbUploadInputRef}
            type="file"
            accept=".glb,model/gltf-binary"
            multiple
            className="d-none"
            onChange={handleGlbUpload}
          />
          <div className="d-flex gap-2 mb-3">
            <Button
              color="primary"
              size="sm"
              disabled={glbUploading}
              onClick={() => glbUploadInputRef.current?.click()}
            >
              {glbUploading ? <Spinner size="sm" /> : "Upload GLB files"}
            </Button>
            <Button
              color="secondary"
              outline
              size="sm"
              disabled={glbLoading}
              onClick={loadGlbInventory}
            >
              {glbLoading ? <Spinner size="sm" /> : "Refresh list"}
            </Button>
          </div>
          {glbLoading && !sortedGlbEntries.length ? (
            <div className="py-4 text-center">
              <Spinner />
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-striped mb-0">
                <thead>
                  <tr>
                    <th>CP code</th>
                    <th>Last updated</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedGlbEntries.map((entry) => (
                    <tr
                      key={entry.cp_code}
                      className={entry.present ? undefined : "text-danger"}
                    >
                      <td>
                        <code>{entry.cp_code}</code>
                        {!entry.present ? (
                          <span className="ms-2 small">Missing</span>
                        ) : null}
                      </td>
                      <td className="small">
                        {entry.present && entry.updated_at
                          ? fmtRelativeTime(entry.updated_at)
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {sortedGlbEntries.length === 0 ? (
                <p className="text-muted small mb-0 mt-2">No catalog products found.</p>
              ) : null}
            </div>
          )}
          {glbError ? (
            <Alert color="danger" className="mt-3 mb-0" fade={false}>
              {glbError}
            </Alert>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            disabled={glbUploading}
            onClick={() => setGlbModalOpen(false)}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default StoreCatalogUpdatesPage;
